import { Button, Divider, Flex, Text, VStack } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import { isProd } from '../../../config';
import { useTemporaryInfoUpdateMutation } from '../../../gql/generated/graphql';
import { useTempInfo } from '../../../hooks/useTempInfo';

enum CreditScore {
  Excellent = 770,
  Great = 700,
  Good = 630,
  Poor = 550,
}

const DEFAULT_OVERRIDE = isProd ? null : true;
const DEFAULT_CREDIT_SCORE = isProd ? null : CreditScore.Excellent;

const DevButton = ({
  condition,
  label,
  action,
}: {
  condition: boolean | null;
  label: string;
  action: () => void;
}) => (
  <Button
    border={condition ? '2px' : 'none'}
    borderColor={condition ? 'oceanBoatBlue' : ''}
    bgColor={condition ? 'oceanBoatBlueBG' : 'grayInput'}
    _hover={{ bgColor: 'oceanBoatBlueBG' }}
    size="sm"
    variant="solid"
    ml={1}
    onClick={action}
  >
    {label}
  </Button>
);

const LoanAppDevToolbar = ({ includeTitle = true }: { includeTitle?: boolean }) => {
  const { info } = useTempInfo();
  const [updateTemporaryInfo] = useTemporaryInfoUpdateMutation();
  const [devPrequalOverride, setDevPrequalOverride] = useState<boolean | null>(DEFAULT_OVERRIDE);
  const [devCreditScore, setDevCreditScore] = useState<number | null>(DEFAULT_CREDIT_SCORE);

  useEffect(() => {
    if (!info?.id) {
      return;
    }

    updateTemporaryInfo({
      variables: {
        info: {
          id: info?.id,
          data: {
            dev_prequal_override: devPrequalOverride,
            dev_prequal_credit_score: devCreditScore,
          },
        },
      },
    });
  }, [devPrequalOverride, devCreditScore, info?.id]);

  if (isProd) return null;

  const updateDevValues = (override: boolean | null, creditScore: number | null) => {
    setDevPrequalOverride(override);
    setDevCreditScore(creditScore);
  };

  return (
    <Flex flexDirection="column" justify="center" align="center">
      {includeTitle && (
        <Text textAlign="center" mb="2">
          Loan App Dev Tools
        </Text>
      )}
      <Flex justify="center" align="center" h="150px" my="25px" gap={3}>
        <VStack
          align="left"
          borderWidth={2}
          borderColor="grayBlueish"
          borderRadius="lg"
          mt="10px"
          p="10px"
          mx="auto"
        >
          <Text textAlign="center" mb="2">
            Prequal Result
          </Text>
          <DevButton
            condition={devPrequalOverride == null}
            label="Off"
            action={() => updateDevValues(null, null)}
          />
          <DevButton
            condition={devPrequalOverride === true}
            label="Success"
            action={() => updateDevValues(true, CreditScore.Excellent)}
          />
          <DevButton
            condition={devPrequalOverride === false}
            label="Fail"
            action={() => updateDevValues(false, null)}
          />
        </VStack>
        {devPrequalOverride && (
          <>
            <Divider
              borderColor="black"
              orientation="vertical"
              style={{
                transition: 'transform 0.5s, opacity 0.5s',
                transform: devPrequalOverride ? 'translateX(0)' : 'translateX(-200px)',
                opacity: devPrequalOverride ? 1 : 0,
              }}
            />
            <VStack
              align="left"
              borderWidth={2}
              borderColor="grayBlueish"
              borderRadius="lg"
              mt="10px"
              p="10px"
              mx="auto"
              style={{
                transition: 'transform 0.5s, opacity 0.5s',
                transform: devPrequalOverride ? 'translateX(0)' : 'translateX(-200px)',
                opacity: devPrequalOverride ? 1 : 0,
              }}
            >
              <Text textAlign="center" mb="2">
                Credit Score
              </Text>
              <DevButton
                condition={devCreditScore === CreditScore.Excellent}
                label="720 ↑"
                action={() => setDevCreditScore(CreditScore.Excellent)}
              />
              <DevButton
                condition={devCreditScore === CreditScore.Great}
                label="680-719"
                action={() => setDevCreditScore(CreditScore.Great)}
              />
              <DevButton
                condition={devCreditScore === CreditScore.Good}
                label="600-679"
                action={() => setDevCreditScore(CreditScore.Good)}
              />
              <DevButton
                condition={devCreditScore === CreditScore.Poor}
                label="599 ↓"
                action={() => setDevCreditScore(CreditScore.Poor)}
              />
            </VStack>
          </>
        )}
      </Flex>
    </Flex>
  );
};

export default LoanAppDevToolbar;
